import {
    Grid,
    Alert,
    Box,
    TextField,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Select,
    MenuItem,
    CircularProgress,
    FormControl,
    Typography
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useState, useEffect } from 'react'
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { getBiocharLabSamplesContaminants, postBiocharLabSamplesContaminants } from '../../../../../actions/rof-burns/rof-actions';

const sampleTypes = [
    { id: 0, name: 'IPS' },
    { id: 1, name: 'RS' }
]

// Every field a new row needs before it can be submitted
const REQUIRED = ['sampleDate', 'sampleType', 'pcb', 'pcddfs', 'pah', 'labReport', 'comments'];

export default function RofContaminants({ contaminants, selectedProject }) {
    const [contaminantsState, setContaminantsState] = useState([])
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    // Saved samples are shown read-only; new entries are added below them
    useEffect(() => {
        setContaminantsState(
            (contaminants?.data || []).map(row => ({
                ...row,
                rowId: uuidv4(),
                pcb: row.pcb ?? '',
                pcddfs: row.pcddfs ?? '',
                pah: row.pah ?? '',
                comments: row.comments ?? '',
                labReport: row.labReportUrl,
                disabled: true,
            }))
        );
    }, [contaminants]);

    const handleAddItem = () => {
        setContaminantsState(prev => {
            const maxSampleNumber = prev.reduce((max, row) => {
                const sampleNumber = parseInt(row.sampleNumber, 10);
                return !isNaN(sampleNumber) && sampleNumber > max ? sampleNumber : max;
            }, 0);
            return [...prev, {
                id: null,
                rowId: uuidv4(),
                sampleNumber: String(maxSampleNumber + 1),
                sampleDate: '',
                sampleType: '',
                pcb: '',
                pcddfs: '',
                pah: '',
                labReport: null,
                comments: '',
            }];
        });
    }

    const handleInputChange = (rowId, field, value) => {
        setContaminantsState(prev =>
            prev.map(row =>
                row.rowId === rowId ? { ...row, [field]: value } : row
            )
        );
    };

    const handleDateSelection = (date, rowId) => {
        handleInputChange(rowId, 'sampleDate', date ? moment(date).format('YYYY-MM-DD') : '');
    };

    const handleSourceDoc = (e, rowId) => {
        handleInputChange(rowId, 'labReport', e.target.files[0] || null);
    };

    const handleSubmitData = () => {
        const newRows = contaminantsState.filter(row => !row.disabled);
        const hasError = newRows.some(row => REQUIRED.some(field => row[field] === '' || row[field] === null));
        setError(hasError);
        if (hasError || newRows.length === 0) return;

        setLoading(true);
        const payload = newRows.map(({ id, sampleNumber, sampleDate, sampleType, pcb, pcddfs, pah, labReport, comments }) => (
            { id, sampleNumber, sampleDate, sampleType, pcb, pcddfs, pah, labReport, comments }
        ));
        dispatch(postBiocharLabSamplesContaminants(selectedProject.id, payload)).then(() => {
            dispatch(getBiocharLabSamplesContaminants(selectedProject.id));
            setLoading(false);
        });
    };

    return (
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Biochar Lab Samples (Contaminants)</Typography>
            <TableContainer component={Paper} sx={{ height: 'auto' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Sample</TableCell>
                            <TableCell align="left">Sample Date</TableCell>
                            <TableCell align="left">Sample type (IPS or RS)</TableCell>
                            <TableCell align="left">PCBs</TableCell>
                            <TableCell align="left">PCDD/Fs</TableCell>
                            <TableCell align="left">PAHs</TableCell>
                            <TableCell align="left">Upload Lab Report</TableCell>
                            <TableCell align="left">Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contaminantsState.map(row => {
                            const isString = typeof row.labReport === 'string';
                            return (
                                <TableRow key={row.rowId}>
                                    <TableCell align="left">{row.sampleNumber}</TableCell>
                                    <TableCell align="left">
                                        <FormControl>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    name="sampleDate"
                                                    value={row.sampleDate ? moment(row.sampleDate, 'YYYY-MM-DD') : null}
                                                    onChange={date => handleDateSelection(date, row.rowId)}
                                                    disabled={row.disabled ?? false}
                                                    format="MM/DD/YYYY"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="sampleType"
                                            value={row.sampleType ?? ''}
                                            disabled={row.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(row.rowId, 'sampleType', e.target.value)}
                                        >
                                            {sampleTypes.map(type => (
                                                <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    {['pcb', 'pcddfs', 'pah'].map(field => (
                                        <TableCell align="left" key={field}>
                                            <TextField
                                                name={field}
                                                value={row[field]}
                                                onChange={e => handleInputChange(row.rowId, field, e.target.value)}
                                                type="number"
                                                disabled={row.disabled ?? false}
                                            />
                                        </TableCell>
                                    ))}
                                    <TableCell align="left">
                                        {isString ? (
                                            <a href={row.labReport} target="_blank" rel="noopener noreferrer">
                                                Report
                                            </a>
                                        ) : (
                                            <TextField
                                                disabled={row.disabled ?? false}
                                                name="labReport"
                                                type="file"
                                                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png' }}
                                                onChange={e => handleSourceDoc(e, row.rowId)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="comments"
                                            value={row.comments}
                                            onChange={e => handleInputChange(row.rowId, 'comments', e.target.value)}
                                            type="text"
                                            disabled={row.disabled ?? false}
                                        />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {error ? <Alert severity='error' sx={{ mb: 2 }}>Please fill out all fields before submitting</Alert> : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pt: 2 }}>
                <Button variant='contained' onClick={handleAddItem}>Add Entry</Button>
                {loading ?
                    <CircularProgress /> :
                    <Button variant='contained' onClick={handleSubmitData}>Submit Data</Button>
                }
            </Box>
            <Box>
                {loading &&
                    <Alert
                        severity='info'
                        sx={{ my: 2 }}>
                        Please be patient during data submission as file uploads can take some time to complete
                    </Alert>
                }
            </Box>
        </Grid>
    )
}
